import { useCallback, useEffect } from "react";
import { formBuilderDefinedRules, getRuleResolver } from "../lib/FormBuilderRules";

/**
 * @param {Object} props
 * @param {string} props.fieldName
 * @param {boolean} props.mandatory
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 */
export const useRemoveMandatoryError = ({ fieldName, mandatory, formProps }) => {
  //Remove mandatory error message when mandatory is toggle off
  useEffect(() => {
    !mandatory && formProps.trigger(fieldName);
  }, [fieldName, mandatory]);
}

/**
 * @param {Object} props
 * @param {string} props.fieldName
 * @param {any} props.defaultValue
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 */
export const useDefaultValueTriggerUpdate = ({ fieldName, defaultValue, formProps, }) => {
  /**
   * Notes for defaultValueTriggerUpdate
   * 
   * forces an update with the default value on init
   * should be true when the initial configs (e.g. mandatory, disabled) another field is dependent on this field in the form
   * this is because react-hook-form does not fire an update when the default value is set, and default values set via <Controller/> are delayed, resulting in unsynced values
   * You may ignore this if the default values are set in useForm()
   */
  useEffect(() => {
    if (defaultValue) {
      formProps.setValue(fieldName, defaultValue);
    }
  }, []);
}

/**
 * @param {import("../lib/FormBuilderRules").definedRulesName[]} rulesName
 * 
 * @returns {import("../lib/FormBuilderRules").Rule[]}
 */
export const getRules = (rulesName) => rulesName?.map((ruleName) => {
  const rule = formBuilderDefinedRules[ruleName];
  if (!rule) throw new Error(`FormBuilder: Unknown rule ${ruleName}`)
  return rule;
});

/**
 * @param {Object} props
 * @param {string} props.fieldName
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 * @param {import("../lib/FormBuilderRules").definedRulesName[]} props.rules
 */
export const useOnBlurRulesTrigger = ({ fieldName, formProps, rules }) => {
  const trigger = useCallback(() => {
    const rulesToTrigger = getRules(rules);
    const value = formProps.getValues(fieldName);

    const results = rulesToTrigger.map(getRuleResolver()).map((rule) => rule(value));

    results.forEach((result) => {
      if (result === true) return;
      formProps.setError(fieldName, { type: "manual", message: result });
    });
  }, [fieldName, formProps, rules]);

  return { onBlurTrigger: trigger };

}